import React, { useEffect, useRef, useState } from 'react';

/**
 * useReveal — the one section-reveal mechanism on the site.
 *
 * The "real event" is the visitor arriving. One IntersectionObserver per
 * element, fires once, then unobserves. No framer-motion: it costs ~60 KB
 * gzipped and buys nothing that a class toggle doesn't do, and the landing
 * route has a hard JS budget.
 *
 * Fails open. If IntersectionObserver is missing, or motion is suppressed,
 * the element renders visible immediately — content is never stranded at
 * opacity 0.
 */
export function useReveal({ threshold = 0.2, rootMargin = '0px 0px -8% 0px' } = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    const reduced =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reduced || typeof IntersectionObserver === 'undefined') {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold, rootMargin }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return [ref, visible];
}

/**
 * Reveal — opacity 0→1 + translateY(16px→0) over --t-reveal, once.
 * `stagger` walks direct children 60ms apart, capped at 5 steps.
 */
export function Reveal({
  children,
  className = '',
  stagger = false,
  delay = 0,
  duration,
  as: Tag = 'div',
  ...rest
}) {
  const [ref, visible] = useReveal();
  const base = stagger ? 'reveal-stagger' : 'reveal';
  const style =
    delay || duration
      ? {
          transitionDelay: delay ? `${delay}ms` : undefined,
          transitionDuration: duration ? `${duration}ms` : undefined,
        }
      : undefined;
  return (
    <Tag
      ref={ref}
      style={style}
      className={`${base}${visible ? ' is-visible' : ''} ${className}`}
      {...rest}
    >
      {children}
    </Tag>
  );
}

/**
 * RevealHairline — a rule that draws itself left-to-right on reveal.
 * The case file assembling itself; the signature motion of the ledgers.
 */
export function RevealHairline({ className = '', delay = 0 }) {
  const [ref, visible] = useReveal();
  return (
    <span
      ref={ref}
      aria-hidden="true"
      className={`block h-px w-full origin-left bg-[color:var(--cp-hairline)] transition-transform duration-500 ease-reveal ${
        visible ? 'scale-x-100' : 'scale-x-0'
      } ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    />
  );
}

/**
 * Section — the page's horizontal rhythm.
 * Gutters 20 / 40 / 64 at the three anchors; content caps at 1200.
 * Band padding is clamp(88px, 12vw, 176px); Hero and Final CTA override
 * with viewport-height rules of their own.
 */
export function Section({
  id,
  children,
  className = '',
  bleed = false,
  tone = 'canvas',
  pad = true,
  labelledBy,
  as: Tag = 'section',
}) {
  const tones = {
    void: 'band-nebula-void',
    canvas: 'band-nebula',
    flat: 'bg-canvas',
    none: '',
  };
  const padding = pad ? 'py-[clamp(88px,12vw,176px)]' : '';

  return (
    <Tag
      id={id}
      aria-labelledby={labelledBy}
      className={`relative isolate overflow-x-clip ${tones[tone]} ${padding} ${className}`}
    >
      {bleed ? (
        children
      ) : (
        <div className="relative mx-auto w-full max-w-[1200px] px-5 md:px-10 xl:px-16">
          {children}
        </div>
      )}
    </Tag>
  );
}

/**
 * SectionMarker — "§ 01 · THE CROSSING".
 * A decorative mono label; the real <h2> carries the accessible name, so
 * this is hidden from assistive tech rather than read out as noise.
 */
export function SectionMarker({ children, className = '' }) {
  return (
    <p aria-hidden="true" className={`cp-mono text-[length:var(--fs-mono)] ${className}`}>
      {children}
    </p>
  );
}

/**
 * HeroStack — the composition grammar as a component.
 * Left-aligned column, always. Centring is reserved for single-numeral
 * poster cards and is forbidden here.
 */
export function HeroStack({ children, className = '' }) {
  return <div className={`flex flex-col items-start text-left ${className}`}>{children}</div>;
}

/**
 * Starfield — the site-wide cosmic layer. Mount once, at the root.
 * Two static tiles, one twinkling pair, two drifting orbs. Zero image
 * bytes; this is why the perf budget survives.
 */
export function Starfield() {
  // overflow-hidden is load-bearing: the orbs are deliberately larger than
  // the viewport and anchored off its edges. Uncontained, they widen the
  // surface the page lays out against. The body must never scroll
  // horizontally at any width.
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-0 overflow-hidden">
      <div className="starfield" />
      <div className="starfield-twinkle" />
      <div className="starfield-twinkle starfield-twinkle-slow" />
      <div className="orb orb-gold" />
      <div className="orb orb-coral" />
    </div>
  );
}

/**
 * Device — a CSS-drawn phone frame around a cropped screen rectangle.
 * Drawn rather than baked into the image: crisper at any DPR, and it drops
 * the payload by more than half. The frame itself never glows — light comes
 * from the data inside the screen.
 */
export function Device({ src, alt, width, height, className = '', priority = false, sizes }) {
  return (
    <div
      className={`relative rounded-[11.4%] p-[2.6%] ${className}`}
      style={{
        background: 'linear-gradient(160deg, #2A2A3E 0%, #14142E 45%, #0D0D1C 100%)',
        boxShadow:
          'inset 0 1px 0 rgba(247,242,233,0.22), 0 0 0 1px rgba(247,242,233,0.10), 0 24px 64px rgba(0,0,0,0.55)',
      }}
    >
      <img
        src={src}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        {...(priority ? { fetchpriority: 'high' } : {})}
        className="block w-full rounded-[9%]"
      />
    </div>
  );
}

export default Section;
